/* ===================================================================
   Integrations -- dedicated top-level screen.

   FAITHFUL PORT (2026-07-03, Corbato) of the Claude Design prototype
   `Rhody Reviews.dc.html` (desktop lines 1228-1278, mobile 2463-2498):
   a sticky header with a green "X of Y connected" pill, a responsive grid
   of ONE-TAP connect cards (logo tile + name + optional CORE badge +
   description + status pill + Connect button), and a bottom "Reviews only
   come from Google" info banner.

   This screen replaces the old thin wrapper around Settings' IntegrationsPanel
   so the /integrations route matches the prototype 1:1. It keeps EVERY wired
   behavior (the panel's data plane is unchanged):
     - Google Business Profile: real OAuth connect (googleConnectUrl) with a
       finish-connecting handoff to /welcome/connect, and baseline review
       import. (The Places name-search is REMOVED -- 2026-07-16, Steve.)
     - Native connectors (Jobber/Square/QuickBooks/Calendly/Follow Up Boss/
       Housecall Pro): real connect_url / disconnect + the paste-your-key
       connect form for key connectors (HCP / FUB).
     - Zapier: real connect + an Advanced disclosure that hides the webhook
       URL / integration key / JSON payload behind a closed-by-default link.

   HARD RULE (feedback_no_dev_code_in_app_ui): these are one-tap connect
   cards. An owner-operator NEVER sees a raw webhook URL, API key, or JSON on
   a card face -- anything technical lives behind the "Advanced" disclosure or
   inside the guided paste-key form. Chat-only support: no phone number here.

   The Settings -> Integrations panel (IntegrationsPanel in Settings.tsx) is
   untouched and still available inside Settings.
   =================================================================== */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VendorLogo, { type VendorId } from '../components/VendorLogo';
import { PasswordInput } from '../components/PasswordInput';
import { onboardingInProgress } from './Onboarding';
import {
  useMe,
  useConnectors,
  useDisconnectConnector,
  useDisconnectGoogle,
  useConnectConnectorKey,
  useConnectConnectorWebhook,
  useSaveConnectorWebhookSecret,
  useImportExistingReviews,
  useIntegrationKey,
  useCreateIntegrationKey,
} from '../hooks';
import {
  startGoogleConnect,
  type ConnectorStatus,
  type BaselineImportResult,
  type GoogleConnectionStatus,
} from '../lib/api';
import '../styles/integrations.css';

/* ---- inline SVG glyphs (ported verbatim from the prototype) ---------- */
const GoogleGlyph = (
  <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.5 12.2c0-.7-.1-1.4-.2-2H12v3.8h5.9a5 5 0 0 1-2.2 3.3v2.7l3.5.001c2-1.9 3.3-4.7 3.3-7.8z" />
    <path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.7l-3.5-2.7c-1 .7-2.3 1.1-3.8 1.1-2.9 0-5.3-1.9-6.2-4.6H2.2v2.8A11 11 0 0 0 12 23z" />
    <path fill="#FBBC04" d="M5.8 14.1a6.6 6.6 0 0 1 0-4.2V7.1H2.2a11 11 0 0 0 0 9.8z" />
    <path fill="#EA4335" d="M12 5.4c1.6 0 3 .6 4.2 1.6l3.1-3.1A11 11 0 0 0 2.2 7.1l3.6 2.8C6.7 7.3 9.1 5.4 12 5.4z" />
  </svg>
);

/* Map engine connector providers to their bundled VendorLogo mark. */
const CONNECTOR_LOGO: Record<string, VendorId> = {
  jobber: 'jobber',
  square: 'square',
  quickbooks: 'quickbooks',
  housecall_pro: 'housecall_icon',
  housecall: 'housecall_icon',
  calendly: 'calendly',
  acuity: 'acuity',
  fub: 'fub',
  // Webhook connectors -- real brand marks (2026-07-30, Brad).
  workiz: 'workiz',
  sweepandgo: 'sweepandgo',
  responsibid: 'responsibid',
};

/* Per-provider copy for the paste-your-key connectors (HCP two-step, FUB
   single-step). Owner-facing plain language only (no raw code). Mirrors the
   Settings KEY_CONNECTOR_UI so one vendor's steps can never bleed into
   another's card. */
type KeyConnectorUi = {
  keyLabel: string;
  instructions: string;
  keyPlaceholder: string;
  emptyKeyMsg: string;
  needsWebhookStep: boolean;
};
const KEY_CONNECTOR_UI: Record<string, KeyConnectorUi> = {
  housecall_pro: {
    keyLabel: 'Housecall Pro API key',
    instructions:
      'In Housecall Pro, go to App Store, open the API card, and click Generate API Key (admin only).',
    keyPlaceholder: 'Paste your API key',
    emptyKeyMsg: 'Paste your Housecall Pro API key.',
    needsWebhookStep: true,
  },
  fub: {
    keyLabel: 'Follow Up Boss API key',
    instructions:
      'In Follow Up Boss, go to Admin > API and copy your API key (account owner or admin).',
    keyPlaceholder: 'Paste your API key',
    emptyKeyMsg: 'Paste your Follow Up Boss API key.',
    needsWebhookStep: false,
  },
};

/* ---- small presentational helpers (prototype card anatomy) ---------- */

/* The 46px grey logo tile. Vendor marks render "bare" (no VendorLogo tile)
   inside our own #f8f9fa square so it matches the prototype exactly. */
function LogoTile({ children }: { children: React.ReactNode }) {
  return <div className="intgx-logo">{children}</div>;
}

type StatusTone = 'on' | 'off' | 'wip';
function StatusPill({ tone, label }: { tone: StatusTone; label: string }) {
  return (
    <span className={`intgx-status intgx-status--${tone}`}>
      <i />
      {label}
    </span>
  );
}

/* The shared card shell: top row (logo + name/badge/desc), then children
   (status row + any inline form). Matches prototype line 1243. */
function ConnectCard({
  logo,
  name,
  core,
  desc,
  connected,
  children,
}: {
  logo: React.ReactNode;
  name: string;
  core?: boolean;
  desc: string;
  /* Drives the hue cap: GREEN = live integration, which is exactly what
     green means everywhere else in the app (see the hue semantics block in
     tokens.css). An unconnected card takes no cap rather than a colour it
     has not earned -- red here would read as "broken", and "not set up yet"
     is not an error. */
  connected?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className={'intgx-card sec-topcap' + (connected ? ' hue-green' : ' hue-none')}>
      <div className="intgx-card__top">
        <LogoTile>{logo}</LogoTile>
        <div className="intgx-card__meta">
          <div className="intgx-name">
            <b>{name}</b>
            {core && <span className="intgx-badge">Core</span>}
          </div>
          <p className="intgx-desc">{desc}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

/* =====================================================================
   Google Business Profile card -- the one CORE, review-source connector.
   Real OAuth connect + finish-connecting handoff + baseline import.
   ===================================================================== */
function GoogleCard({
  clientId,
  status,
  tokenValid,
  listingConfirmed,
  loading,
}: {
  clientId: string | null;
  status: GoogleConnectionStatus;
  tokenValid: boolean;
  listingConfirmed: boolean;
  loading: boolean;
}) {
  const navigate = useNavigate();
  const importReviews = useImportExistingReviews();
  const disconnectGoogle = useDisconnectGoogle();
  const [importResult, setImportResult] = useState<BaselineImportResult | null>(null);
  const [importErr, setImportErr] = useState<string | null>(null);

  // Disconnect / change-listing: the way out when the WRONG Google listing is
  // confirmed. Two-step (a text link -> an explicit confirm) so a single mis-tap
  // can't detach a working connection. On success the parent's /me refetch drops
  // listingConfirmed back to false, so this card returns to its finish-connecting
  // state where the owner re-picks the correct listing via the connect flow.
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [disconnectErr, setDisconnectErr] = useState<string | null>(null);

  async function handleDisconnect() {
    setDisconnectErr(null);
    try {
      await disconnectGoogle.disconnect();
      // Clear this card's local echoes so nothing stale flashes before the
      // parent's invalidated /me resolves.
      setImportResult(null);
      setImportErr(null);
      setConfirmDisconnect(false);
    } catch (e) {
      setDisconnectErr(
        e instanceof Error
          ? e.message
          : "We couldn't disconnect that listing. Try again in a moment.",
      );
    }
  }

  const runImport = useCallback(async () => {
    setImportErr(null);
    setImportResult(null);
    try {
      const res = await importReviews.importReviews();
      setImportResult(res);
    } catch (e) {
      setImportErr(e instanceof Error ? e.message : 'Import failed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function connectGoogle() {
    if (!clientId) return;
    void startGoogleConnect(clientId);
  }

  // CANONICAL connection state. "Connected" renders ONLY at status ===
  // 'connected'; token-valid-but-unconfirmed is an honest "Finish connecting
  // Google" (wip), never a green Connected pill.
  const fullyConnected = status === 'connected';
  const tone: StatusTone = fullyConnected ? 'on' : tokenValid ? 'wip' : 'off';
  const statusLabel = fullyConnected
    ? 'Connected'
    : tokenValid
      ? 'Finish connecting Google'
      : 'Not connected';
  const desc = fullyConnected
    ? 'Connected. We read, draft replies for, and import your Google reviews.'
    : 'Connect to read, reply to, and import your Google reviews.';

  return (
    <ConnectCard logo={GoogleGlyph} name="Google Business Profile" core desc={desc} connected={fullyConnected}>
      <div className="intgx-statusrow">
        <div className="intgx-statuswrap">
          <StatusPill tone={tone} label={statusLabel} />
        </div>
        {!tokenValid ? (
          <button
            className="intgx-btn intgx-btn--primary"
            onClick={connectGoogle}
            disabled={!clientId || loading}
          >
            {loading ? 'Loading...' : 'Connect'}
          </button>
        ) : listingConfirmed ? (
          <button
            className="intgx-btn intgx-btn--ghost"
            onClick={runImport}
            disabled={importReviews.isPending}
          >
            {importReviews.isPending ? 'Importing...' : 'Import reviews'}
          </button>
        ) : null}
      </div>

      {/* Token valid but no confirmed listing yet: hand off to the ONE minimal
          connect flow (/welcome/connect). It resolves the businesses the owner's
          Google account manages -- auto-connecting a single listing -- with the
          paste-your-review-link fallback. The Places name-search that used to
          live here is REMOVED (2026-07-16, Steve): it returns ZERO_RESULTS for a
          service-area business, permanently, so it only dead-ended owners. */}
      {tokenValid && !listingConfirmed && (
        <div className="intgx-form">
          <b>One step left</b>
          <p>Your Google account is linked. Finish by picking your business.</p>
          <button
            className="intgx-btn intgx-btn--primary"
            onClick={() => navigate('/welcome/connect')}
          >
            Finish connecting Google
          </button>
        </div>
      )}

      {importErr && <p className="intgx-err">{importErr}</p>}
      {importResult && (
        <div style={{ fontSize: 12, lineHeight: 1.5 }}>
          <b>Imported {importResult.total_imported} review(s).</b>
        </div>
      )}

      {/* Disconnect / change listing -- ONLY once a listing is confirmed. This
          is the escape hatch for an owner who connected the wrong Google
          business. Nulls the connection (incl. the reply-posting target) so the
          card drops back to search + the owner re-picks. Two-step confirm. */}
      {listingConfirmed && (
        <div className="intgx-form">
          {!confirmDisconnect ? (
            <button
              className="intgx-btn intgx-btn--ghost"
              onClick={() => {
                setDisconnectErr(null);
                setConfirmDisconnect(true);
              }}
              disabled={disconnectGoogle.isPending}
            >
              Disconnect / change listing
            </button>
          ) : (
            <>
              <b>Disconnect this Google listing?</b>
              <p>
                This lets you connect a different business. Your business details
                are kept, and nothing is sent to your customers.
              </p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  className="intgx-btn intgx-btn--primary"
                  onClick={handleDisconnect}
                  disabled={disconnectGoogle.isPending}
                >
                  {disconnectGoogle.isPending ? 'Disconnecting...' : 'Yes, disconnect it'}
                </button>
                <button
                  className="intgx-btn intgx-btn--ghost"
                  onClick={() => setConfirmDisconnect(false)}
                  disabled={disconnectGoogle.isPending}
                >
                  Keep it connected
                </button>
              </div>
            </>
          )}
          {disconnectErr && <p className="intgx-err">{disconnectErr}</p>}
        </div>
      )}
    </ConnectCard>
  );
}

/* =====================================================================
   Key connector card (HCP two-step / FUB single-step) -- paste-your-key
   FORM, never OAuth. All key-generation help + webhook detail lives INSIDE
   the guided form, not on the card face (HARD RULE: no dev code up front).
   ===================================================================== */
function KeyConnectorCard({ c }: { c: ConnectorStatus }) {
  const connectKey = useConnectConnectorKey();
  const saveSecret = useSaveConnectorWebhookSecret();
  const disconnect = useDisconnectConnector();
  const logoId = CONNECTOR_LOGO[c.provider];
  const note = c.needs && 'requirement_note' in c.needs ? c.needs.requirement_note : '';
  const helpUrl = c.needs && 'key_help_url' in c.needs ? c.needs.key_help_url : undefined;

  const ui: KeyConnectorUi = KEY_CONNECTOR_UI[c.provider] ?? {
    keyLabel: `${c.label} API key`,
    instructions: note || `Paste your ${c.label} API key to connect.`,
    keyPlaceholder: 'Paste your API key',
    emptyKeyMsg: `Paste your ${c.label} API key.`,
    needsWebhookStep: false,
  };

  const [open, setOpen] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [webhookUrl, setWebhookUrl] = useState<string | null>(c.webhook_url);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const active = ui.needsWebhookStep ? c.connected && !!c.webhook_secret_saved : c.connected;
  const awaitingSecret = ui.needsWebhookStep && c.connected && !c.webhook_secret_saved;
  const showForm = (open || awaitingSecret) && !active;
  const url = webhookUrl || c.webhook_url;

  async function submitKey() {
    setErr(null);
    if (!apiKey.trim()) {
      setErr(ui.emptyKeyMsg);
      return;
    }
    try {
      const res = await connectKey.connectKey(c.provider, apiKey.trim());
      setWebhookUrl(res.webhook_url);
      setApiKey('');
      if (!ui.needsWebhookStep) setOpen(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save that key. Check it and try again.');
    }
  }

  async function submitSecret() {
    setErr(null);
    if (!secret.trim()) {
      setErr(`Paste the signing secret ${c.label} gave you.`);
      return;
    }
    try {
      await saveSecret.saveSecret(c.provider, secret.trim());
      setSecret('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not save the signing secret. Try again.');
    }
  }

  function copyUrl() {
    if (!url) return;
    void navigator.clipboard?.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  const tone: StatusTone = active ? 'on' : awaitingSecret ? 'wip' : 'off';
  const statusLabel = active
    ? 'Connected'
    : awaitingSecret
      ? 'Almost there -- add the webhook'
      : 'Not connected';

  return (
    <ConnectCard
      logo={logoId ? <VendorLogo id={logoId} size={26} bare /> : <span>{c.label[0]}</span>}
      name={c.label}
      desc={c.description}
      connected={active}
    >
      <div className="intgx-statusrow">
        <div className="intgx-statuswrap">
          <StatusPill tone={tone} label={statusLabel} />
          {note && <div className="intgx-note">{note}</div>}
        </div>
        {active ? (
          <button
            className="intgx-btn intgx-btn--ghost"
            onClick={() => disconnect.disconnect(c.provider)}
            disabled={disconnect.isPending}
          >
            Disconnect
          </button>
        ) : (
          <button className="intgx-btn intgx-btn--primary" onClick={() => setOpen((o) => !o)}>
            {c.connected ? 'Finish setup' : 'Connect'}
          </button>
        )}
      </div>
      {disconnect.error && (
        <p className="intgx-err">Could not disconnect. Please try again.</p>
      )}

      {showForm && (
        <div className="intgx-form">
          {!c.connected && (
            <>
              <b>{ui.needsWebhookStep ? 'Step 1: paste your API key' : 'Paste your API key'}</b>
              <p>
                {ui.instructions}{' '}
                {helpUrl && (
                  <a href={helpUrl} target="_blank" rel="noreferrer">
                    How do I find this?
                  </a>
                )}
              </p>
              <div className="intgx-field">
                <span className="intgx-lab">{ui.keyLabel}</span>
                <PasswordInput
                  className="intgx-input"
                  autoComplete="off"
                  placeholder={ui.keyPlaceholder}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
              </div>
              <button
                className="intgx-btn intgx-btn--primary"
                onClick={submitKey}
                disabled={connectKey.isPending || !apiKey.trim()}
              >
                {connectKey.isPending ? 'Saving...' : 'Save key'}
              </button>
            </>
          )}

          {ui.needsWebhookStep && c.connected && url && (
            <>
              <b>Step 2: add the webhook in Housecall Pro</b>
              <p>
                In Housecall Pro, add this webhook URL and turn on the "job.completed"
                event. Copy it exactly:
              </p>
              <div className="intgx-copyrow" style={{ marginBottom: 10 }}>
                <input className="intgx-input" readOnly value={url} style={{ flex: 1 }} />
                <button className="intgx-btn intgx-btn--ghost" onClick={copyUrl}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p>Housecall Pro will give you a signing secret. Paste it here to finish:</p>
              <div className="intgx-field">
                <span className="intgx-lab">Webhook signing secret</span>
                <PasswordInput
                  className="intgx-input"
                  autoComplete="off"
                  placeholder="Paste the signing secret"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </div>
              <button
                className="intgx-btn intgx-btn--primary"
                onClick={submitSecret}
                disabled={saveSecret.isPending || !secret.trim()}
              >
                {saveSecret.isPending ? 'Finishing...' : 'Finish connecting'}
              </button>
            </>
          )}

          {err && <p className="intgx-err">{err}</p>}

          {c.connected && (
            <button
              className="intgx-btn intgx-btn--ghost"
              style={{ marginTop: 10 }}
              onClick={() => disconnect.disconnect(c.provider)}
              disabled={disconnect.isPending}
            >
              Disconnect
            </button>
          )}
        </div>
      )}
    </ConnectCard>
  );
}

/* =====================================================================
   Webhook connector card (Workiz / Sweep&Go / ResponsiBid) -- the guided
   "copy this URL into the vendor" connect. These vendors have no API key and
   no OAuth; the ONLY connect model is: we reveal a per-tenant inbound webhook
   URL, the owner pastes it into the vendor's webhook setting following the
   numbered steps. "Connect" creates the connector row so the receiver accepts
   the vendor's pushes; the URL + steps + copy button live on the card face
   (HARD RULE: no raw JSON, no dev codes -- a URL + a copy button + plain steps).
   ===================================================================== */
function WebhookConnectorCard({ c }: { c: ConnectorStatus }) {
  const connectWebhook = useConnectConnectorWebhook();
  const disconnect = useDisconnectConnector();
  const logoId = CONNECTOR_LOGO[c.provider];

  // The per-tenant webhook URL is returned on the connector status (webhook_url)
  // for webhook connectors, and again on connect. Prefer whichever we have.
  const [url, setUrl] = useState<string | null>(c.webhook_url);
  const [steps, setSteps] = useState<string[]>(c.steps ?? []);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // The guided panel opens when the owner starts the connect flow, and can be
  // reopened later from "Show setup steps".
  //
  // It used to be `open || c.connected`, i.e. permanently expanded once
  // connected. That was the last LAYOUT leak of the webhook-vs-OAuth
  // distinction (Brad, 2026-08-06): a connected Workiz card stayed a tall
  // open panel showing its URL forever, while every connected OAuth card is a
  // compact row. A returning owner still needs the URL occasionally, so it
  // moves behind a disclosure -- the same pattern the Zapier card already uses
  // on this page -- instead of shouting on every visit.
  const showPanel = open;
  const effectiveUrl = url || c.webhook_url;
  const effectiveSteps = steps.length ? steps : (c.steps ?? []);

  async function connect() {
    setErr(null);
    try {
      const res = await connectWebhook.connectWebhook(c.provider);
      setUrl(res.webhook_url);
      setSteps(res.steps);
      setOpen(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start the connection. Please try again.');
    }
  }

  function copyUrl() {
    if (!effectiveUrl) return;
    void navigator.clipboard?.writeText(effectiveUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  // SAME WORDS AS EVERY OTHER CARD (Steve, 2026-08-06). The connector TYPE used
  // to leak into the owner-facing chrome here: the pill said "Webhook ready"
  // and the button said "Connect Workiz" while every OAuth card says
  // "Connected" and "Connect". The owner does not care which mechanism a vendor
  // uses -- the promise is identical, so the language is identical. The
  // MECHANICS still differ (this starts the guided copy-the-URL flow instead of
  // an OAuth redirect); only the wording is unified.
  const tone: StatusTone = c.connected ? 'on' : 'off';
  const statusLabel = c.connected ? 'Connected' : 'Not connected';

  return (
    <ConnectCard
      logo={logoId ? <VendorLogo id={logoId} size={26} bare /> : <span>{c.label[0]}</span>}
      name={c.label}
      desc={c.description}
      connected={c.connected}
    >
      <div className="intgx-statusrow">
        <div className="intgx-statuswrap">
          <StatusPill tone={tone} label={statusLabel} />
          {c.requirement_note && <div className="intgx-note">{c.requirement_note}</div>}
        </div>
        {c.connected ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* Reopen the copy-the-URL steps without leaving the card expanded
                for everyone, every visit. */}
            <button
              className="intgx-btn intgx-btn--ghost"
              onClick={() => setOpen((v) => !v)}
            >
              {open ? 'Hide setup steps' : 'Show setup steps'}
            </button>
            <button
              className="intgx-btn intgx-btn--ghost"
              onClick={() => disconnect.disconnect(c.provider)}
              disabled={disconnect.isPending}
            >
              Disconnect
            </button>
          </div>
        ) : (
          <button
            className="intgx-btn intgx-btn--primary"
            onClick={connect}
            disabled={connectWebhook.isPending}
          >
            {connectWebhook.isPending ? 'Connecting...' : 'Connect'}
          </button>
        )}
      </div>

      {showPanel && (
        <div className="intgx-form">
          <b>Paste this webhook URL into {c.label}</b>
          <p>
            Copy the URL below, then follow the steps to add it in {c.label}. When
            a job is completed, {c.label} tells us and we ask that customer for a
            Google review.
          </p>
          <div className="intgx-copyrow" style={{ marginBottom: 12 }}>
            <input className="intgx-input" readOnly value={effectiveUrl ?? ''} style={{ flex: 1 }} />
            <button className="intgx-btn intgx-btn--ghost" onClick={copyUrl} disabled={!effectiveUrl}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {effectiveSteps.length > 0 && (
            <ol className="intgx-steps">
              {effectiveSteps.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ol>
          )}
          {c.connected && (
            <p className="intgx-ok">
              Connected. Once you add the URL in {c.label}, completed jobs will
              start requesting reviews automatically.
            </p>
          )}
        </div>
      )}
      {err && <p className="intgx-err">{err}</p>}
      {disconnect.error && (
        <p className="intgx-err">Could not disconnect. Please try again.</p>
      )}
    </ConnectCard>
  );
}

/* =====================================================================
   OAuth connector card (Jobber / Square / QuickBooks / Calendly).
   One-tap connect_url / disconnect. Registered-but-data-layer-not-built
   shows an honest "Setup in progress" status rather than "Connected".
   ===================================================================== */
function OAuthConnectorCard({ c }: { c: ConnectorStatus }) {
  const disconnect = useDisconnectConnector();
  const logoId = CONNECTOR_LOGO[c.provider];
  const wip = c.data_layer_live === false && !c.connected;

  const tone: StatusTone = c.connected ? 'on' : wip ? 'wip' : 'off';
  const statusLabel = c.connected ? 'Connected' : wip ? 'Setup in progress' : 'Not connected';

  return (
    <ConnectCard
      logo={logoId ? <VendorLogo id={logoId} size={26} bare /> : <span>{c.label[0]}</span>}
      name={c.label}
      desc={c.description}
      connected={c.connected}
    >
      <div className="intgx-statusrow">
        <div className="intgx-statuswrap">
          <StatusPill tone={tone} label={statusLabel} />
          {c.requirement_note && <div className="intgx-note">{c.requirement_note}</div>}
        </div>
        {c.connected ? (
          <button
            className="intgx-btn intgx-btn--ghost"
            onClick={() => disconnect.disconnect(c.provider)}
            disabled={disconnect.isPending}
          >
            Disconnect
          </button>
        ) : (
          <button
            className="intgx-btn intgx-btn--primary"
            onClick={() => {
              if (c.connect_url) window.location.href = c.connect_url;
            }}
            disabled={!c.connect_url}
          >
            Connect
          </button>
        )}
      </div>
      {disconnect.error && (
        <p className="intgx-err">Could not disconnect. Please try again.</p>
      )}
    </ConnectCard>
  );
}

/* =====================================================================
   Zapier card -- one-tap "Connect", with the developer webhook URL / key /
   JSON payload hidden behind a CLOSED-by-default "Advanced" disclosure.
   99% of owners never open it (HARD RULE: no dev code on the card face).
   ===================================================================== */
const ZAPIER_CONNECT_URL =
  'https://zapier.com/developer/public-invite/243303/1192fe875fc5a76635ed2b0aae123a82/';

function ZapierCard() {
  const q = useIntegrationKey();
  const create = useCreateIntegrationKey();
  const [advanced, setAdvanced] = useState(false);
  const [freshKey, setFreshKey] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const webhookUrl = q.data?.webhook_url ?? '';
  const hasKey = q.data?.has_key ?? false;

  async function generate() {
    setCopied(null);
    try {
      const res = await create.create(true);
      setFreshKey(res.integration_key);
    } catch {
      /* surfaced by disabled state */
    }
  }

  function copy(label: string, value: string) {
    void navigator.clipboard?.writeText(value);
    setCopied(label);
    window.setTimeout(() => setCopied(null), 1500);
  }

  return (
    <ConnectCard
      logo={<VendorLogo id="zapier" size={24} bare />}
      name="Zapier"
      desc="Use the tool you already have to connect 6,000+ apps. We add the customer and queue a review request automatically."
      connected={hasKey}
    >
      <div className="intgx-statusrow">
        <div className="intgx-statuswrap">
          <StatusPill tone={hasKey ? 'on' : 'off'} label={hasKey ? 'Ready' : 'Not connected'} />
        </div>
        <button
          className="intgx-btn intgx-btn--primary"
          onClick={() => window.open(ZAPIER_CONNECT_URL, '_blank', 'noopener,noreferrer')}
        >
          Connect
        </button>
      </div>

      <button
        className="intgx-adv-toggle"
        onClick={() => setAdvanced((s) => !s)}
        aria-expanded={advanced}
      >
        {advanced ? 'Hide advanced' : 'Advanced -- for developers / your Zapier helper'}
      </button>

      {advanced && (
        <div className="intgx-adv">
          <p style={{ margin: '0 0 12px', color: '#5f6368' }}>
            For a developer or your Zapier helper: point any tool at this webhook URL
            with the key as a Bearer token. We do the rest.
          </p>
          <div className="intgx-fld">
            <span className="intgx-lab">Webhook URL</span>
            <div className="intgx-copyrow">
              <code style={{ flex: 1 }}>{webhookUrl || '...'}</code>
              <button
                className="intgx-btn intgx-btn--ghost"
                onClick={() => copy('url', webhookUrl)}
                disabled={!webhookUrl}
              >
                {copied === 'url' ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          <div className="intgx-fld">
            <span className="intgx-lab">Integration key (Authorization: Bearer ...)</span>
            {freshKey ? (
              <div className="intgx-copyrow">
                <code style={{ flex: 1 }}>{freshKey}</code>
                <button className="intgx-btn intgx-btn--ghost" onClick={() => copy('key', freshKey)}>
                  {copied === 'key' ? 'Copied' : 'Copy'}
                </button>
              </div>
            ) : (
              <p style={{ margin: 0, color: '#5f6368' }}>
                {hasKey
                  ? 'A key exists. Generate a new one to reveal it (this rotates the old key).'
                  : 'No key yet. Generate one to authenticate your webhook.'}
              </p>
            )}
            <button
              className="intgx-btn intgx-btn--primary"
              style={{ marginTop: 8 }}
              onClick={generate}
              disabled={create.isPending}
            >
              {create.isPending ? 'Generating...' : hasKey ? 'Rotate key' : 'Generate key'}
            </button>
            {create.error && (
              <p className="intgx-err" style={{ marginTop: 6 }}>
                Could not generate a key just now. Please try again.
              </p>
            )}
            {freshKey && (
              <p style={{ marginTop: 6, color: '#5f6368' }}>
                Copy this key now. For security it is shown only once.
              </p>
            )}
          </div>

          <div className="intgx-fld">
            <span className="intgx-lab">Payload (POST JSON)</span>
            <pre>
{`{
  "customer": {
    "first_name": "Jane",
    "last_name": "Doe",
    "email": "jane@example.com",
    "phone": "+14015551234"
  },
  "job_ref": "INV-1234"
}`}
            </pre>
            <p style={{ margin: 0, color: '#5f6368' }}>
              Sending a completed job upserts the customer and queues a review request
              (no message is sent until SMS is enabled).
            </p>
          </div>
        </div>
      )}
    </ConnectCard>
  );
}

/* =====================================================================
   Screen root -- sticky header (with connected-count pill), the connect
   grid, and the bottom info banner. Prototype lines 1228-1278 / 2463-2498.
   ===================================================================== */
export function Integrations() {
  const me = useMe();
  const navigate = useNavigate();
  const clientId = me.data?.client_id ?? null;
  // CANONICAL connection state (single source of truth). Only status ===
  // 'connected' counts as connected in the "X of Y connected" pill + the
  // Google card; token/listing signals drive the intermediate sub-steps.
  const gStatus = me.data?.google_connection_status ?? 'not_started';
  const tokenValid = !!me.data?.oauth_token_valid;
  const listingConfirmed = !!me.data?.listing_confirmed;
  const googleFullyConnected = gStatus === 'connected';
  // GBP connection HEALTH (2026-08-03). When the tenant's stored Google refresh
  // token has died (revoked / expired / undecryptable), the daily health sweep
  // flags google_needs_reconnect. Surface a prominent, honest reconnect prompt
  // so the owner fixes it instead of the connection failing silently.
  const needsReconnect = !!me.data?.google_needs_reconnect;
  const connectors = useConnectors();
  const [returnFlag, setReturnFlag] = useState<string | null>(null);

  // Read the OAuth return flag ONCE on mount and clear it from the URL so a
  // refresh doesn't replay it. U2: if the owner reached Google-connect FROM the
  // onboarding wizard (an in-progress onboarding is saved for this tenant),
  // send them straight back into the wizard at the Connect step with Google now
  // connected -- instead of stranding them here on /integrations.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('google');
    if (!flag) return;
    // U2: the onboarding-resume decision needs the tenant id (to look up the
    // saved wizard). Wait until /me resolves clientId before consuming the
    // param, so we don't strip it and miss the resume on the first paint.
    if (clientId === null) return;
    if (onboardingInProgress(clientId)) {
      // Phase 3: resume the unified checklist's Connect task with Google now
      // linked (was the legacy ?step=1 linear index).
      navigate('/onboarding?task=connect', { replace: true });
      return;
    }
    setReturnFlag(flag);
    params.delete('google');
    params.delete('detail');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
  }, [clientId, navigate]);

  // Fixed display order matching Steve's spec (Jobber, Square, then the tall
  // key cards, then QuickBooks). Any provider not named keeps backend order.
  const CONNECTOR_ORDER = [
    'jobber', 'square', 'calendly', 'fub', 'housecall_pro', 'housecall', 'quickbooks',
    'workiz', 'sweepandgo', 'responsibid',
  ];
  const orderOf = (p: string) => {
    const i = CONNECTOR_ORDER.indexOf(p);
    return i === -1 ? CONNECTOR_ORDER.length : i;
  };
  const conns: ConnectorStatus[] = [...(connectors.data?.connectors ?? [])]
    .filter(
      (c) =>
        c.connected ||
        (c.configured && !!c.connect_url) ||
        c.connect_form ||
        c.connect_kind === 'webhook',
    )
    .sort((a, b) => orderOf(a.provider) - orderOf(b.provider));

  // "X of Y connected" -- count real connected connectors (Google + natives).
  const nativeConnected = conns.filter((c) => c.connected).length;
  const connectedCount = (googleFullyConnected ? 1 : 0) + nativeConnected;
  const total = 1 /* Google */ + conns.length + 1 /* Zapier */;

  return (
    <div className="intgx">
      <div className="intgx-head">
        <div>
          <h1>Integrations</h1>
          <p>Connect the tools you already use so requests and reviews flow on their own.</p>
        </div>
        <div className="intgx-count">
          <i />
          {connectedCount} of {total} connected
        </div>
      </div>

      <div className="intgx-body">
        {/* Connection-health reconnect prompt (2026-08-03). Shown when the daily
            health sweep found this tenant's Google refresh token dead. One tap
            re-runs the same OAuth connect flow, which re-mints a live token. */}
        {needsReconnect && clientId && (
          <div className="intgx-banner intgx-banner--err" role="alert">
            <strong>Reconnect your Google account.</strong> Your Google connection
            stopped working, so we can't import new reviews or post replies until
            you reconnect. It only takes a moment.
            <div style={{ marginTop: 10 }}>
              <button
                className="intgx-btn intgx-btn--primary"
                onClick={() => {
                  void startGoogleConnect(clientId);
                }}
              >
                Reconnect Google
              </button>
            </div>
          </div>
        )}

        {/* Post-OAuth return banners. */}
        {returnFlag === 'scope_insufficient' && clientId && (
          <div className="intgx-banner intgx-banner--err" role="alert">
            <strong>You signed in, but didn't grant Business Profile access.</strong>{' '}
            Reconnect and approve "Manage your Google Business Profile" so we can
            import your reviews and post replies. Nothing was connected.
            <div style={{ marginTop: 10 }}>
              <button
                className="intgx-btn intgx-btn--primary"
                onClick={() => {
                  void startGoogleConnect(clientId);
                }}
              >
                Reconnect Google
              </button>
            </div>
          </div>
        )}
        {returnFlag === 'error' && (
          <div className="intgx-banner intgx-banner--err" role="alert">
            That Google connection didn't finish. Click Connect to try again.
          </div>
        )}
        {returnFlag === 'connected' && (
          <div className="intgx-banner intgx-banner--ok" role="status">
            {/* Truthful: the ?google=connected OAuth flag only means the token
                saved + a candidate listing was auto-found -- NOT that the
                canonical status is 'connected' or that reviews imported. Assert
                importing ONLY when the real status says connected. */}
            {googleFullyConnected
              ? 'Google connected. Your reviews are in.'
              : 'Google linked. Confirm your business below to finish connecting and import your reviews.'}
          </div>
        )}
        {returnFlag === 'needs_business' && (
          <div className="intgx-banner intgx-banner--ok" role="status">
            Google connected. One quick step: confirm your business below so we can find
            your reviews.
          </div>
        )}

        <div className="intgx-grid">
          <GoogleCard
            clientId={clientId}
            status={gStatus}
            tokenValid={tokenValid}
            listingConfirmed={listingConfirmed}
            loading={me.isLoading}
          />

          {conns.map((c) =>
            c.connect_kind === 'webhook' ? (
              <WebhookConnectorCard key={c.provider} c={c} />
            ) : c.connect_form ? (
              <KeyConnectorCard key={c.provider} c={c} />
            ) : (
              <OAuthConnectorCard key={c.provider} c={c} />
            ),
          )}

          <ZapierCard />
        </div>

        {/* Bottom info banner (prototype line 1272). */}
        <div className="intgx-info">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1a73e8"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 16v-4M12 8h.01" />
          </svg>
          <div>
            <strong>Reviews only come from Google.</strong> The other tools here send
            requests and sync your customer list. They don't add new review sources. Your
            rating always reflects Google alone.
          </div>
        </div>
      </div>
    </div>
  );
}
